import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Maximize2, Minimize2, ArrowLeft } from "lucide-react";

const fmt = (n, dec=2) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

function Row({ item, big }) {
  const dir = item?.dir?.satis_dir || "";
  const color = dir === "up" ? "text-emerald-400" : dir === "down" ? "text-red-400" : "text-white";
  const arrow = dir === "up" ? "▲" : dir === "down" ? "▼" : "";
  const dec = item?.selling > 100 ? 2 : 4;
  const unit = item?.unit || "₺";
  return (
    <div className={`grid grid-cols-3 gap-4 items-baseline py-3 border-b border-white/5 ${big?"text-2xl md:text-3xl py-5 md:py-6":"text-lg"}`}>
      <div className="label-caps text-sm">{item?.label || item?.code}</div>
      <div className={`font-mono tabular-nums text-right ${color} ${big?"text-3xl md:text-5xl":""}`}>{arrow} {fmt(item?.selling, dec)} <span className="text-[0.55em] text-neutral-400 ml-1">{unit}</span></div>
      <div className="font-mono tabular-nums text-right text-neutral-500 text-sm md:text-base">{fmt(item?.buying, dec)} {unit}</div>
    </div>
  );
}

export default function Signage() {
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const [karats, setKarats] = useState({});
  const [fx, setFx] = useState({});
  const [ts, setTs] = useState("");
  const [now, setNow] = useState(new Date());
  const [isFs, setIsFs] = useState(false);
  const [ctrlVisible, setCtrlVisible] = useState(true);
  const idleTimer = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [k, f] = await Promise.all([api.get("/karats"), api.get("/fx")]);
        setKarats(k.data.karats || {});
        setFx(f.data.pairs || {});
        setTs(k.data.last_updated || "");
      } catch (e) {}
    };
    load();
    const iv = setInterval(load, 2500);
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(iv); clearInterval(clock); };
  }, []);

  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // Idle fade — controls appear on mouse/touch, hide after 2.5s of no activity.
  useEffect(() => {
    const bump = () => {
      setCtrlVisible(true);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setCtrlVisible(false), 2500);
    };
    bump();
    window.addEventListener("mousemove", bump);
    window.addEventListener("touchstart", bump);
    window.addEventListener("keydown", bump);
    return () => {
      window.removeEventListener("mousemove", bump);
      window.removeEventListener("touchstart", bump);
      window.removeEventListener("keydown", bump);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, []);

  const goBack = async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch {}
    nav("/dashboard");
  };

  const toggleFs = async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
      else await document.exitFullscreen();
    } catch {}
  };

  const order = ["ALTIN","AYAR22","AYAR14","CEYREK_YENI","YARIM_YENI","TAM_YENI","ATA_YENI","ONS","XPTUSD","PLATIN_GRAM_TL"];
  const items = order.map(c => karats[c]).filter(Boolean);
  const fxItems = ["USDTRY","EURTRY","GBPTRY","CHFTRY"].map(c => fx[c]).filter(Boolean);
  const clockStr = new Intl.DateTimeFormat(locale, {timeStyle:"medium"}).format(now);
  const dateStr = new Intl.DateTimeFormat(locale, {dateStyle:"full"}).format(now);

  return (
    <div className="fixed inset-0 z-50 bg-[#050505] p-6 md:p-10 lg:p-14 overflow-auto" data-testid="signage-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="font-display text-3xl md:text-5xl lg:text-6xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
          <div className="label-caps mt-2 text-sm md:text-base">{t("signage.title")} · Kapalıçarşı</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-3xl md:text-5xl lg:text-6xl tabular-nums" style={{color:"var(--gold)"}} data-testid="signage-clock">{clockStr}</div>
          <div className="label-caps mt-1 text-xs md:text-sm">{dateStr}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10">
        <div className="lg:col-span-2">
          <div className="label-caps mb-4">Altın · Ziynet</div>
          <div className="grid grid-cols-3 gap-4 label-caps text-neutral-500 border-b border-white/10 pb-3">
            <div>Ürün</div><div className="text-right">Satış ₺</div><div className="text-right">Alış ₺</div>
          </div>
          {items.map(k => <Row key={k.code} item={k} big />)}
        </div>

        <div>
          <div className="label-caps mb-4">Döviz</div>
          <div className="grid grid-cols-3 gap-4 label-caps text-neutral-500 border-b border-white/10 pb-3">
            <div>Parite</div><div className="text-right">Satış</div><div className="text-right">Alış</div>
          </div>
          {fxItems.map(k => <Row key={k.code} item={k} />)}
          <div className="mt-8 p-5 border border-[--gold]/30 text-center" style={{background:"rgba(212,175,55,0.05)"}}>
            <div className="label-caps mb-2">Son Güncelleme</div>
            <div className="font-mono text-lg" style={{color:"var(--gold)"}}>
              {ts ? new Intl.DateTimeFormat(locale,{timeStyle:"medium"}).format(new Date(ts)) : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Idle-fade controls: shimmering gold ‘Panel'e Dön’ + fullscreen toggle */}
      <div
        className={`fixed top-4 right-4 sm:top-6 sm:right-6 flex gap-2 items-center transition-all duration-500 ease-out ${
          ctrlVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        data-testid="signage-controls"
      >
        <button
          onClick={goBack}
          data-testid="signage-back-to-panel"
          className="group relative inline-flex items-center gap-2 pl-3 pr-4 py-2.5 border border-[--gold] backdrop-blur-md bg-black/60 hover:bg-[--gold] transition-colors duration-200"
          style={{ color: "var(--gold)", borderColor: "var(--gold)" }}
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5 group-hover:text-black" />
          <span className="text-xs font-mono tracking-wider uppercase group-hover:text-black">Panel'e Dön</span>
        </button>
        <button
          onClick={toggleFs}
          data-testid="signage-fullscreen"
          title={isFs ? "Tam ekrandan çık" : "Tam ekran"}
          className="p-2.5 bg-black/60 border border-white/20 hover:border-[--gold] backdrop-blur-md transition-colors"
        >
          {isFs ? <Minimize2 size={14} style={{color:"var(--gold)"}} /> : <Maximize2 size={14} style={{color:"var(--gold)"}} />}
        </button>
      </div>
    </div>
  );
}
